/**
 * Lecture bornée d’une archive ZIP (conteneur OOXML) issue d’un fichier non fiable.
 *
 * Le répertoire central est analysé AVANT toute décompression : nombre d’entrées, tailles
 * déclarées, taux de compression, chiffrement, ZIP64 et doublons de noms sont contrôlés.
 * La décompression ne cible que les entrées demandées, dans un tampon de la taille déclarée,
 * puis la taille réellement obtenue est comparée à la taille déclarée (en-tête mensonger = refus).
 */
import { Inflate } from 'fflate';
import { XLSX_LIMITS, XlsxImportError } from './XlsxLimits';

interface EntreeZip {
  name: string;
  flags: number;
  method: number;
  compressedSize: number;
  size: number;
  localOffset: number;
}

const SIG_EOCD = 0x06054b50;
const SIG_CD = 0x02014b50;

function lireEntrees(data: Uint8Array): EntreeZip[] {
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (data.byteLength < 22) throw new XlsxImportError('Archive .xlsx invalide (trop courte)');
  let eocd = -1;
  const min = Math.max(0, data.byteLength - 22 - 0xffff);
  for (let i = data.byteLength - 22; i >= min; i--) {
    if (dv.getUint32(i, true) === SIG_EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0)
    throw new XlsxImportError('Archive .xlsx invalide (répertoire central introuvable)');
  const nb = dv.getUint16(eocd + 10, true);
  const tailleCd = dv.getUint32(eocd + 12, true);
  const offsetCd = dv.getUint32(eocd + 16, true);
  if (nb === 0xffff || tailleCd === 0xffffffff || offsetCd === 0xffffffff) {
    throw new XlsxImportError('Archive ZIP64 non prise en charge');
  }
  if (nb > XLSX_LIMITS.maxEntries) {
    throw new XlsxImportError(
      `Classeur refusé : plus de ${XLSX_LIMITS.maxEntries} fichiers internes`,
    );
  }
  if (offsetCd + tailleCd > data.byteLength) {
    throw new XlsxImportError('Archive .xlsx invalide (répertoire central hors limites)');
  }
  const noms = new Set<string>();
  const entrees: EntreeZip[] = [];
  let p = offsetCd;
  const decodeur = new TextDecoder('utf-8', { fatal: false });
  for (let i = 0; i < nb; i++) {
    if (p + 46 > data.byteLength || dv.getUint32(p, true) !== SIG_CD) {
      throw new XlsxImportError('Archive .xlsx invalide (entrée corrompue)');
    }
    const flags = dv.getUint16(p + 8, true);
    const method = dv.getUint16(p + 10, true);
    const compressedSize = dv.getUint32(p + 20, true);
    const size = dv.getUint32(p + 24, true);
    const lenNom = dv.getUint16(p + 28, true);
    const lenExtra = dv.getUint16(p + 30, true);
    const lenCom = dv.getUint16(p + 32, true);
    const localOffset = dv.getUint32(p + 42, true);
    if (p + 46 + lenNom > data.byteLength) {
      throw new XlsxImportError('Archive .xlsx invalide (nom d’entrée hors limites)');
    }
    const name = decodeur.decode(data.subarray(p + 46, p + 46 + lenNom));
    p += 46 + lenNom + lenExtra + lenCom;
    if (compressedSize === 0xffffffff || size === 0xffffffff) {
      throw new XlsxImportError('Archive ZIP64 non prise en charge');
    }
    if (name.includes('..') || name.startsWith('/') || name.includes('\\') || name.includes('\0')) {
      throw new XlsxImportError('Classeur refusé : chemin interne suspect');
    }
    if (noms.has(name)) throw new XlsxImportError('Classeur refusé : entrées en double');
    noms.add(name);
    entrees.push({ name, flags, method, compressedSize, size, localOffset });
  }
  return entrees;
}

/**
 * Retourne le contenu décompressé des entrées dont le nom satisfait `voulu`.
 * Contrôles de taille appliqués sur TOUTES les entrées de l’archive.
 */
export function lireZipBorne(
  data: Uint8Array,
  voulu: (nom: string) => boolean,
): { noms: string[]; fichiers: Map<string, Uint8Array> } {
  const entrees = lireEntrees(data);
  let total = 0;
  for (const e of entrees) {
    if ((e.flags & 1) !== 0) {
      throw new XlsxImportError('Classeur protégé par mot de passe (chiffré) : non pris en charge');
    }
    if (e.method !== 0 && e.method !== 8) {
      throw new XlsxImportError('Classeur refusé : méthode de compression non prise en charge');
    }
    if (e.size > XLSX_LIMITS.maxEntryBytes) {
      throw new XlsxImportError('Classeur refusé : contenu décompressé trop volumineux');
    }
    total += e.size;
    if (total > XLSX_LIMITS.maxTotalBytes) {
      throw new XlsxImportError('Classeur refusé : contenu décompressé trop volumineux');
    }
    if (
      e.size > 1024 * 1024 &&
      e.size > Math.max(1, e.compressedSize) * XLSX_LIMITS.maxCompressionRatio
    ) {
      throw new XlsxImportError('Classeur refusé : taux de compression anormal');
    }
  }
  const fichiers = new Map<string, Uint8Array>();
  for (const e of entrees) {
    if (!voulu(e.name)) continue;
    fichiers.set(e.name, extraire(data, e));
  }
  return { noms: entrees.map((e) => e.name), fichiers };
}

const SIG_LOCAL = 0x04034b50;
/** Taille des morceaux fournis au décompresseur : borne la sortie d’un seul pas à ~4 Mio. */
const MORCEAU = 4096;

function extraire(data: Uint8Array, e: EntreeZip): Uint8Array {
  const dv = new DataView(data.buffer, data.byteOffset, data.byteLength);
  const o = e.localOffset;
  if (o + 30 > data.byteLength || dv.getUint32(o, true) !== SIG_LOCAL) {
    throw new XlsxImportError('Archive .xlsx invalide (en-tête local corrompu)');
  }
  const debut = o + 30 + dv.getUint16(o + 26, true) + dv.getUint16(o + 28, true);
  const fin = debut + e.compressedSize;
  if (fin > data.byteLength)
    throw new XlsxImportError('Archive .xlsx invalide (données tronquées)');
  const brut = data.subarray(debut, fin);
  if (e.method === 0) {
    if (e.compressedSize !== e.size)
      throw new XlsxImportError('Archive .xlsx corrompue (taille incohérente)');
    return brut.slice();
  }
  const sortie = new Uint8Array(e.size);
  let ecrit = 0;
  let termine = false;
  const inf = new Inflate((morceau, final) => {
    if (ecrit + morceau.byteLength > e.size) {
      throw new XlsxImportError('Archive .xlsx corrompue (taille déclarée dépassée)');
    }
    sortie.set(morceau, ecrit);
    ecrit += morceau.byteLength;
    if (final) termine = true;
  });
  try {
    for (let i = 0; i < brut.byteLength; i += MORCEAU) {
      inf.push(
        brut.subarray(i, Math.min(i + MORCEAU, brut.byteLength)),
        i + MORCEAU >= brut.byteLength,
      );
    }
    if (brut.byteLength === 0) inf.push(new Uint8Array(0), true);
  } catch (err) {
    if (err instanceof XlsxImportError) throw err;
    throw new XlsxImportError('Archive .xlsx invalide ou corrompue');
  }
  if (!termine || ecrit !== e.size) {
    throw new XlsxImportError('Archive .xlsx corrompue (taille déclarée incohérente)');
  }
  return sortie;
}
